// ToDo: switch off branch name env var to build production url on master
const API_BASE = (process.env.NODE_ENV === "production" && process.env.REACT_APP_API_URL) || "";
export const API_URL = `${API_BASE}/api`;

/**
 * A failed API call, as an Error.
 *
 * `getData` used to reject with the raw `Response`. Callers overwhelmingly write
 * `error instanceof Error ? error.message : "<fallback>"`, so every HTTP failure took the fallback
 * branch and the server's reason — which the API does send as `{ error: { message } }` — was
 * discarded before any screen could show it. A route rejecting an enrolment or a survey version
 * surfaced to the practitioner as a bare status code.
 *
 * `status` is kept as a field so the few callers that inspected the Response's status still work.
 */
export class ApiError extends Error {
    readonly status: number;
    readonly body: unknown;

    constructor(message: string, status: number, body?: unknown) {
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.body = body;
    }
}

/**
 * Pull the human-readable reason out of a failed response, falling back to the status.
 *
 * Reads the body ONCE — a Response body is a single-use stream, so this must not be called twice
 * on the same response. Any shape the API actually uses is accepted; anything else degrades to the
 * status line rather than showing the user a JSON fragment.
 */
export const errorFromResponse = async (response: Response, action?: string): Promise<ApiError> => {
    const fallback = action
        ? `Failed to ${action} (${response.status})`
        : `Request failed (${response.status} ${response.statusText})`.trim();

    let body: unknown;
    try {
        body = await response.clone().json();
    } catch {
        return new ApiError(fallback, response.status);
    }

    const record = body as { error?: { message?: unknown } | string; message?: unknown };
    const message =
        (typeof record?.error === "object" && typeof record.error?.message === "string" && record.error.message) ||
        (typeof record?.error === "string" && record.error) ||
        (typeof record?.message === "string" && record.message) ||
        null;

    return new ApiError(message || fallback, response.status, body);
};

let headers = {
    "Content-Type": "application/json",
};

const setHeader = (name: string, data: string) => {
    headers = { ...headers, [name]: data };
};

const getHeaders = () => {
    const token = localStorage.getItem("token");
    if (token) {
        setHeader("x-auth-token", token);
    }
    return headers;
};

const postData = async (url = ``, data = {}): Promise<Response> =>
    await fetch(`${API_URL}${url}`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: getHeaders(),
    });

// Special handler for FormData uploads (doesn't JSON.stringify)
const postFormData = async (url = ``, formData: FormData): Promise<Response> => {
    const token = localStorage.getItem("token");
    const headers: HeadersInit = {};
    if (token) {
        headers["x-auth-token"] = token;
    }
    // Don't set Content-Type for FormData - browser will set it with boundary
    return await fetch(`${API_URL}${url}`, {
        method: "POST",
        body: formData,
        headers,
        credentials: "include",
    });
};

const putData = async (url = ``, data = {}): Promise<Response> =>
    await fetch(`${API_URL}${url}`, {
        method: "PUT",
        body: JSON.stringify(data),
        headers: getHeaders(),
    });

const patchData = async (url = ``, data = {}): Promise<Response> =>
    await fetch(`${API_URL}${url}`, {
        method: "PATCH",
        body: JSON.stringify(data),
        headers: getHeaders(),
    });

const getData = async <T>(url = ``): Promise<T> => {
    try {
        const response = await fetch(`${API_URL}${url}`, {
            method: "GET",
            headers: getHeaders(),
        });

        if (response.ok) {
            return await response.json();
        }

        return Promise.reject(await errorFromResponse(response));
    } catch (error) {
        return Promise.reject(error);
    }
};

const getFile = async (url = ``): Promise<void> => {
    const response = await fetch(`${API_URL}${url}`, {
        method: "GET",
        headers: getHeaders(),
    }).then(async (res) => ({
        filename: res.headers.get("content-disposition")?.split("filename=")[1],
        blob: await res.blob(),
    }));

    // MS Edge and IE don't allow using a blob object directly as link href, instead it is necessary to use msSaveOrOpenBlob
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const nav = window.navigator as any;
    if (nav && nav.msSaveOrOpenBlob) {
        nav.msSaveOrOpenBlob(response.blob);
        return;
    }

    // For other browsers: create a link pointing to the ObjectURL containing the blob.
    const objUrl = window.URL.createObjectURL(response.blob);

    const link = document.createElement("a");
    link.href = objUrl;
    link.download = response.filename ?? "file";
    link.click();
};

const deleteData = async (url = ``): Promise<Response> =>
    await fetch(`${API_URL}${url}`, {
        method: "DELETE",
        headers: getHeaders(),
    });

export { postData, postFormData, getData, patchData, putData, deleteData, getFile };
